import { BaseController } from '../../../../common/base/controller';
import _ from 'lodash';

export class TicketTableHeaderController extends BaseController {
  constructor(
    $filter,
    $scope,
    controllerService,
    ticketService,
    ticketTableService,
    ticketFlowService,
    TicketLists,
    RelevantTicketStatesForTransferring,
    RelevantTicketStatesForReceiving,
    TicketStatesSiblings,
    SearchSuggestions,
    SearchSuggestTemplate,
    $transitions,
  ) {
    'ngInject';
    super({
      $filter,
      controllerService,
      ticketService,
      ticketTableService,
      ticketFlowService,
      TicketLists,
      RelevantTicketStatesForTransferring,
      RelevantTicketStatesForReceiving,
      TicketStatesSiblings,
      SearchSuggestions,
      $transitions,
    });

    this.model = {
      search: '',
      searchInputs: [],
    };

    this.searchSuggest = {
      templateUrl: SearchSuggestTemplate,
      editable: true,
      delay: 200,
    };

    // Create debounced input handler which debounces subsequent calls
    // and ensures that `input` is invoked once after 1 second of debounced calls.
    this.debouncedChange = _.debounce(
      () => this.onChange({ options: this.model }),
      200,
      { maxWait: 1000 },
    );

    // Live search
    let searchPromise;
    $scope.$watch('vm.model.search', (value) => {
      this.$timeout.cancel(searchPromise);
      searchPromise = this.$timeout(this.debouncedChange, 200);
    });

    $scope.$watch('vm.downloadMonth', (val) => {
      // on init, the value is not set. we dont want to init a download from here
      if (!val) return;
      this.showDownloadMonthPicker = false;
      this.downloadProgression(val);
    });
  }

  $onDestroy() {
    // Reset search model
    this.resetSearch();
  }

  get hideCreateNew() {
    return this.isDWS || this.isKWS;
  }

  get ticketLists() {
    return this.TicketLists;
  }

  get hasCapturingPermission() {
    return !this.userService.isDataCapturingProvider();
  }

  get canDownloadProgression() {
    const allowedRoles = [
      this.userService.Roles.BackOffice,
      this.userService.Roles.BackOfficeDirect,
      this.userService.Roles.Compliance,
      this.userService.Roles.Revision,
    ];
    return (
      this.userService.hasRole(allowedRoles) &&
      !this.isFeatureActive(
        this.FeatureNames.FF_DISABLE_TICKET_LIST_CSV_DOWNLOAD,
      )
    );
  }

  get canDownloadBafinReport() {
    return (
      this.hasCapturingPermission &&
      this.isFeatureActive(this.FeatureNames.FF_CAN_DOWNLOAD_BAFIN_REPORT) &&
      !this.isDWS
    );
  }

  get downloadReportingMonthBased() {
    return this.isFeatureActive(
      this.FeatureNames.FF_SPLIT_TICKET_PROGRESSION_MONTHLY,
    );
  }

  downloadProgression(since) {
    this.onDownloadProgression({ since });
  }

  downloadBafin() {
    this.onDownloadBafin();
  }

  // kwh-light feature
  get showTicketTableSearch() {
    return !this.isFeatureActive(
      this.FeatureNames.FF_DISABLE_TICKET_LIST_SEARCH,
    );
  }

  $onInit() {
    this.tableStateKey = this.ticketTableService.getTableStateKey(
      this.ticketType,
    );

    if (this.tableState && this.tableState.filter) {
      const { filterValues, liveSearch, filterType } = this.tableState.filter;
      this.model.searchInputs = filterValues;
      this.model.search = liveSearch;

      this.setExternalSearchTerms();

      return (this.model.ticketList = _.find(
        this.ticketLists,
        (tl) => filterType && tl.label === filterType.label,
      ));
    }

    this.setExternalSearchTerms();

    // Set first ticket list initially
    this.activateList(
      this.isKWS ? _.last(this.ticketLists) : _.first(this.ticketLists),
    );

    // because of multiple products url changes have to be watched
    this.$transitions.onSuccess({}, (transition) => {
      this.resetSearch();
      this.model.ticketList = this.isKWS
        ? _.last(this.ticketLists)
        : _.first(this.ticketLists);
      this.onChange({ options: this.model });
    });
  }

  setExternalSearchTerms() {
    if (
      this.externalSearchTerm &&
      this.externalSearchTerm.some((e, i) => {
        return e.value !== this.model.searchInputs[i];
      })
    ) {
      for (let i in this.externalSearchTerm) {
        this.addSearch(this.externalSearchTerm[i]);
      }
    }
  }

  activateList(ticketList) {
    this.model.ticketList = ticketList;
    this.debouncedChange();
  }

  isListActive(ticketList) {
    return this.model.ticketList == ticketList;
  }

  searchSuggestSource($viewValue = '') {
    // add translated states to suggestions
    const suggestions = this.enrichSearchSuggestionsWithStates();

    // add ibans if search value has at least 3 chars
    if ($viewValue && $viewValue.length > 2) {
      suggestions.ibans = _.reduce(
        this.ibans,
        (ibans, iban) => {
          if (iban && iban.toLowerCase().indexOf($viewValue) > -1) {
            ibans.push({
              value: iban,
            });
          }
          return ibans;
        },
        [],
      );
    }

    // flatten suggestion object to one dimensional array
    // use key as type, add isFirst property
    return [{ type: 'text', value: $viewValue }].concat(
      _.reduce(
        suggestions,
        (acc, val, key) => {
          // filter suggestions depending on view value
          const filtered = this.$filter('filter')(val, $viewValue);
          const s = _.map(filtered, (element, index) => {
            if (index === 0) {
              element.isFirst = true;
            }
            element.type = key;
            return element;
          });
          return acc.concat(s);
        },
        [],
      ),
    );
  }

  enrichSearchSuggestionsWithStates() {
    // Create search suggestions only once by:
    if (!this.searchSuggestionStates) {
      // 1. Get the relevant states for each ticket type
      this.relevantStates = {
        transferring: this.RelevantTicketStatesForTransferring,
        receiving: this.RelevantTicketStatesForReceiving,
      };

      // 2. Include only the relevant ticket states
      let states = _.intersection(
        _.values(this.ticketFlowService.getStatesForType(this.ticketType)),
        this.relevantStates[this.ticketType],
      );

      // 3. Remove the ticket siblings
      let filteredStates = _.filter(states, (s) => {
        return (
          _.flattenDeep(Object.values(this.TicketStatesSiblings)).indexOf(s) ===
          -1
        );
      });

      // 4. Apply ticket states translations
      const status = _.map(filteredStates, (key) => {
        const value = this.$translate.instant(`app.ticket.status.${key}`);
        return { key, value };
      });

      // 5. Add hard coded search suggestions
      this.searchSuggestionStates = _.merge({}, this.SearchSuggestions, {
        status,
      });
    }

    return this.searchSuggestionStates;
  }

  searchSuggestSelect($item) {
    const { type, key, value, valueAddition } = $item;
    this.addSearch({ type, key, value, valueAddition });
  }

  addSearch(search) {
    if (!search) {
      return;
    }

    if (!this.model.searchInputs) {
      this.model.searchInputs = [];
    }

    this.model.search = '';

    // if searchInputs contains search term yet return.
    if (_.filter(this.model.searchInputs, search).length) {
      return;
    }

    this.model.searchInputs.push(search);
    // this.updateTableState();
    this.debouncedChange();
    this.forceClearSearch();
  }

  removeSearch(search) {
    _.remove(this.model.searchInputs, search);
    _.remove(this.searchTerm, search);
    // this.updateTableState();
    this.debouncedChange();
  }

  forceClearSearch() {
    this.$timeout(() => {
      this.model.search = '';
    });
  }

  resetSearch() {
    this.model.search = '';
    this.model.searchInputs = [];
  }

  // NOTE: This is not used anymore, but we keep it for reference.
  // This method overwrites all other previous table state values.
  // updateTableState() {
  //   this.ticketTableService.setTableState(this.tableStateKey, {
  //     filter: {
  //       filterValues: this.model.searchInputs,
  //       liveSearch: this.model.search,
  //       filterType: this.model.ticketList,
  //     },
  //   });
  // }

  downloadMonthPickerOptions = {
    datepickerMode: 'month',
    minMode: 'month',
  };
}
